import { bridge } from '../bridge'
import { markOutdated } from '../store/outdated'
import { clientHeaderValue } from './clientHeader'
import { serverBaseURL } from './config'
import { keychain } from './keychain'
import type { PendingInvite } from '../store/types'

// Action declares (resource, action) for the call. The header rides on
// every authenticated HTTP call so future IAM can plug into one
// authorize() function. Spec:
// hearth-cmd/docs/forward-compat-resource-scope-tuple.md.
export type Action = { kind: string; id?: string; action: string }

function actionHeader(a: Action): string {
  return `${a.kind}:${a.id ?? ''}:${a.action}`
}

export class APIError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`http ${status}: ${body}`)
  }
}

async function doFetch(
  path: string,
  action: Action,
  init: RequestInit & { auth?: 'device' | 'session'; sessionToken?: string } = {},
): Promise<Response> {
  const base = await serverBaseURL()
  // Empty base = same-origin (dev proxy). Non-empty = absolute host.
  const url = base === '' ? path : new URL(path, base).toString()
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')
  headers.set('X-Hearth-Client', await clientHeaderValue())
  headers.set('X-Hearth-Action', actionHeader(action))

  if (init.auth === 'session' && init.sessionToken) {
    headers.set('Authorization', `Bearer ${init.sessionToken}`)
  }
  if (init.auth === 'device') {
    const ioDeviceID = await keychain.ioDeviceID()
    const secret = await keychain.ioDeviceSecret()
    if (!ioDeviceID || !secret) {
      throw new APIError(401, 'not_enrolled')
    }
    headers.set('Authorization', `Bearer ${secret}`)
    headers.set('X-IO-Device-ID', ioDeviceID)
  }

  const res = await fetch(url, { ...init, headers })
  // 426 Upgrade Required is the version-handshake sentinel. Surface the
  // outdated state to the global store so the blocking modal renders;
  // throw so the caller's normal error path doesn't try to use the body.
  if (res.status === 426) {
    let body: { min_version?: string; your_version?: string; update_url?: string } = {}
    try {
      body = await res.clone().json()
    } catch {
      // body wasn't JSON; fields stay empty and the modal still renders
    }
    markOutdated({
      minVersion: body.min_version ?? '',
      yourVersion: body.your_version ?? '',
      updateURL: body.update_url,
    })
    throw new APIError(426, 'client_outdated')
  }
  return res
}

// fetchAuthedBlobURL fetches an authenticated binary resource (e.g. a chat
// attachment) with device auth and returns an object URL suitable for an
// <img src> — an <img> can't carry auth headers, so we fetch the bytes here and
// hand back a blob URL. Caller owns the returned URL's lifetime (revoke when
// done), though the attachment cache keeps them for the session since
// attachments are immutable.
export async function fetchAuthedBlobURL(path: string, action: Action): Promise<string> {
  const res = await doFetch(path, action, { method: 'GET', auth: 'device' })
  if (!res.ok) {
    throw new APIError(res.status, await res.text().catch(() => ''))
  }
  const blob = await res.blob()
  return URL.createObjectURL(blob)
}

async function postJSON<T>(
  path: string,
  body: unknown,
  action: Action,
  init: { auth?: 'device' | 'session'; sessionToken?: string } = {},
): Promise<T> {
  const res = await doFetch(path, action, {
    method: 'POST',
    body: JSON.stringify(body),
    ...init,
  })
  const text = await res.text()
  if (!res.ok) throw new APIError(res.status, text)
  return text ? (JSON.parse(text) as T) : ({} as T)
}

// ---- Auth / enrollment --------------------------------------------------

export async function requestAuthCode(email: string): Promise<void> {
  // Server returns 200 regardless of outcome (anti-enumeration). We still
  // surface network errors so the UI can show "couldn't reach server".
  await postJSON<void>(
    '/auth/request',
    { email: email.trim().toLowerCase() },
    { kind: 'human_user', action: 'request_otp' },
  )
}

export async function verifyAuthCode(
  email: string,
  code: string,
  purpose: 'enroll_device' | 'enroll_host' | 'step_up',
): Promise<{ sessionToken: string; isNewUser: boolean; hasPendingInvites: boolean }> {
  const { session_token, is_new_user, has_pending_invites } = await postJSON<{
    session_token: string
    is_new_user?: boolean
    has_pending_invites?: boolean
  }>(
    '/auth/verify',
    { email: email.trim().toLowerCase(), code, purpose },
    { kind: 'human_user', action: 'verify_otp' },
  )
  return {
    sessionToken: session_token,
    isNewUser: Boolean(is_new_user),
    // Set when this address has been invited somewhere. Used to skip the
    // "name your household" step — enrollment won't create one for an
    // invitee, so asking would discard the answer.
    hasPendingInvites: Boolean(has_pending_invites),
  }
}

export async function enrollDevice(args: {
  sessionToken: string
  ioDeviceID: string
  mode: 'fresh' | 'reclaim'
  deviceName: string
  formFactor: 'phone' | 'tablet' | 'browser'
  // attestationToken is the platform-appropriate device-attestation
  // payload — DeviceCheck JWT on iOS, Play Integrity token on Android,
  // null on web (no browser equivalent; server accepts type=none under
  // the dev build tag). Server routes by `platform` field below.
  attestationToken: string | null
  isSandbox: boolean
  platform: 'ios' | 'android' | 'web'
  organizationID?: string
  userName?: string
  organizationName?: string
}): Promise<{
  io_device_id: string
  device_secret: string
  human_user_id: string
  organization_id: string
  // Invitations addressed to the verified email, resolved server-side
  // during enrollment. Lets a brand-new device render the join offer on
  // its first screen, before it has a WebSocket to ask over. When this
  // is non-empty the server deliberately did NOT mint a household for
  // the user — they choose, and creating their own is the fallback.
  pending_invites?: PendingInvite[]
}> {
  // Build the typed attestation envelope. Spec:
  // hearth-cmd/docs/forward-compat-attestation-envelope.md.
  // - iOS with a token → devicecheck (payload carries is_sandbox)
  // - Android with a token → play_integrity
  // - web, or any platform without a token → "none" (server only
  //   accepts this when its dev flag is set; prod web policy is TBD —
  //   see docs/web-as-product-surface.md)
  let attestation: { type: string; payload?: Record<string, unknown> }
  if (args.platform === 'ios' && args.attestationToken) {
    attestation = {
      type: 'devicecheck',
      payload: { token: args.attestationToken, is_sandbox: args.isSandbox },
    }
  } else if (args.platform === 'android' && args.attestationToken) {
    attestation = {
      type: 'play_integrity',
      payload: { token: args.attestationToken },
    }
  } else {
    attestation = { type: 'none' }
  }

  const body = {
    io_device_id: args.ioDeviceID,
    mode: args.mode,
    device_name: args.deviceName,
    form_factor: args.formFactor,
    platform: args.platform,
    attestation,
    organization_id: args.organizationID,
    user_name: args.userName,
    organization_name: args.organizationName,
  }
  return postJSON(
    '/devices/enroll',
    body,
    { kind: 'io_device', id: args.ioDeviceID, action: 'enroll' },
    { auth: 'session', sessionToken: args.sessionToken },
  )
}

// ---- Invites ------------------------------------------------------------

export async function sendInvite(args: {
  organizationID: string
  email: string
}): Promise<{ invite_id: string; expires_at: string }> {
  return postJSON(
    '/invites',
    { organization_id: args.organizationID, email: args.email.trim().toLowerCase() },
    { kind: 'invite', action: 'create' },
    { auth: 'device' },
  )
}

// acceptInvite (POST /invites/accept) and peekInvite (GET /invites/:token)
// lived here. Both were bearer-token operations and are gone:
//   - Acceptance moved to the `accept_invites` ws_request, which works
//     from the caller's own pending list and needs no token.
//   - The peek endpoint was deleted server-side; it disclosed a household
//     name and an email address to anyone holding a forwarded link.
// See api/ws.ts → "My invitations (invitee side)".

// ---- Permission response (fallback path; primary is WS) -----------------

export async function sendPermissionResponse(args: {
  requestID: string
  behavior: 'allow' | 'deny' | 'deny_stop' | 'always_allow' | 'always_deny'
  expiresAt?: string | null
  interrupt?: boolean
  selectedOptionID?: string
  freeText?: string
}): Promise<void> {
  const ioDeviceID = await keychain.ioDeviceID()
  const secret = await keychain.ioDeviceSecret()
  if (!ioDeviceID || !secret) throw new APIError(401, 'not_enrolled')
  // Carry the full payload so the HTTP fallback is faithful to the WS path —
  // expiry (temporary grants), interrupt, and the picker's selection / free
  // text. The server runs it through the same handlePermissionResponse.
  await postJSON<void>(
    '/response',
    {
      io_device_id: ioDeviceID,
      secret,
      request_id: args.requestID,
      behavior: args.behavior,
      ...(args.expiresAt ? { expires_at: args.expiresAt } : {}),
      ...(args.interrupt ? { interrupt: true } : {}),
      ...(args.selectedOptionID ? { selected_option_id: args.selectedOptionID } : {}),
      ...(args.freeText ? { free_text: args.freeText } : {}),
    },
    { kind: 'permission_request', id: args.requestID, action: args.behavior },
  )
}

// ---- Pro feature subscriptions -----------------------------------------

// Submits a StoreKit V2 signed transaction (Transaction.jwsRepresentation)
// to the server, claiming the subscription for the given organization.
// Server verifies the JWS against Apple's pinned root cert, upserts a row
// in pro_feature_subscriptions, and pushes a fresh organizations_list so
// the UI's is_pro flips. Idempotent on the underlying original_transaction_id;
// safe to re-call across restarts/restores.
export async function submitProSubscription(args: {
  signedTransaction: string
  organizationID: string
  platform?: 'apple' | 'google'
  productId?: string | null
}): Promise<{ status: string; expires_at: string }> {
  const ioDeviceID = await keychain.ioDeviceID()
  const secret = await keychain.ioDeviceSecret()
  if (!ioDeviceID || !secret) throw new APIError(401, 'not_enrolled')
  const body: Record<string, unknown> = {
    io_device_id: ioDeviceID,
    secret,
    platform: args.platform ?? 'apple',
    signed_transaction: args.signedTransaction,
    organization_id: args.organizationID,
  }
  if (args.productId) body.product_id = args.productId
  return postJSON(
    '/subscriptions/submit',
    body,
    { kind: 'pro_subscription', id: args.organizationID, action: 'submit' },
  )
}

// ---- Logout -------------------------------------------------------------

// Tells the server to revoke this device's io_device and (optionally) its
// paired host. Hits POST /logout — no step-up required since the caller
// already holds the secret it's asking to kill. Caller wipes the local
// keychain afterwards.
export async function logoutDevice(): Promise<void> {
  const ioDeviceID = await keychain.ioDeviceID()
  const secret = await keychain.ioDeviceSecret()
  if (!ioDeviceID || !secret) return
  await postJSON<void>(
    '/logout',
    {},
    { kind: 'io_device', id: ioDeviceID, action: 'logout' },
    { auth: 'device' },
  )
}

// ---- Enrollment probe ---------------------------------------------------

// Confirms whether this device's credentials are still accepted by the
// server. GET /devices just lists the caller's own enrollments, so its
// only failure mode is "these credentials are no longer valid" — there is
// no resource-authorization dimension that could 401/403 a still-enrolled
// device. That's what lets us distinguish a remote unenroll ('revoked')
// from the server or a host merely being unreachable ('inconclusive').
//
// This exists because the browser WebSocket API hides the handshake's HTTP
// status: a /ws rejected with 403 surfaces to JS only as close code 1006,
// indistinguishable from a network drop. So a revoked device would sit in
// the app forever showing "disconnected" and silently reconnecting. An
// explicit HTTP probe is the deterministic signal the socket can't give us.
export async function probeEnrollment(): Promise<'enrolled' | 'revoked' | 'inconclusive'> {
  try {
    const res = await doFetch('/devices', { kind: 'io_device', action: 'list' }, {
      method: 'GET',
      auth: 'device',
    })
    if (res.ok) return 'enrolled'
    // 401/403 = credentials rejected → the device was revoked/unenrolled.
    if (res.status === 401 || res.status === 403) return 'revoked'
    // 5xx / anything else = server trouble, not an auth verdict.
    return 'inconclusive'
  } catch (err) {
    // Missing local creds (doFetch throws APIError 401 'not_enrolled') means
    // there is nothing enrolled to begin with — treat as revoked so the
    // caller signs out cleanly. Any other throw is a network failure.
    if (err instanceof APIError && err.status === 401) return 'revoked'
    return 'inconclusive'
  }
}

// ---- Step-up auth -------------------------------------------------------

// Device-authed step-up: verifies the OTP via /auth/step_up (not /auth/verify)
// and returns a short-lived step-up token for sensitive operations.
// Callers first call requestAuthCode(email) to trigger the OTP email, then
// call this once the user enters the code.
export async function requestStepUpToken(email: string, code: string): Promise<string> {
  const { stepup_token } = await postJSON<{ stepup_token: string }>(
    '/auth/step_up',
    { email: email.trim().toLowerCase(), code },
    { kind: 'human_user', action: 'step_up' },
    { auth: 'device' },
  )
  return stepup_token
}

// ---- Shared-device pairing (claim) --------------------------------------

// Claim a pending shared-device pairing (e.g. a Home Assistant voice satellite
// that ran /pair/start and is showing a 6-digit code). Device-authed as this
// trusted phone — the claim is the whole trust boundary. Creates the shared
// io_device owned by the caller, in `organizationID` (defaults server-side to
// the caller's default org when empty). See relay pairing.go handlePairClaim.
export async function claimSharedDevice(args: {
  code: string
  organizationID?: string
  name?: string
  // Claimer's choice for a display screen: ephemeral/throwaway. Omitted → the
  // pairing's own value stands (relay side). Ignored for non-display devices.
  isTemp?: boolean
  // Claim the device as PERSONAL (bound to me) rather than SHARED (household-wide).
  // Omitted → shared (unchanged behavior). See docs/personal-devices-plan.md.
  personal?: boolean
  // Voice only: pin the puck's agent at claim time (a position id). Omitted → set later.
  routePositionID?: string
  // Display only: RE-PAIR this browser into an existing screen (the target's io_device
  // id) instead of making a new one — keeps that screen's rules. Owner/member-gated on
  // the target (relay). When set, personal/isTemp/name are ignored (the existing
  // screen's shape stands). See docs/personal-devices-plan.md.
  reclaimScreenID?: string
}): Promise<{
  io_device_id: string
  name: string
  form_factor: string
  organization_id: string
  personal: boolean
  // True when the claim re-paired an existing screen: the one picked in
  // reclaimScreenID, or, with nothing picked, the screen the browser proved it
  // used to be (relay pairing.go, priorScreenMatches). name is then that screen's.
  reclaimed?: boolean
}> {
  try {
    return await postJSON(
      '/pair/claim',
      {
        code: args.code.trim(),
        organization_id: args.organizationID ?? '',
        name: args.name?.trim() ?? '',
        ...(args.isTemp === undefined ? {} : { is_temp: args.isTemp }),
        ...(args.personal ? { personal: true } : {}),
        ...(args.routePositionID ? { route_position_id: args.routePositionID } : {}),
        ...(args.reclaimScreenID ? { reclaim_screen_id: args.reclaimScreenID } : {}),
      },
      { kind: 'io_device', action: 'claim' },
      { auth: 'device' },
    )
  } catch (e) {
    // Turn the raw "http 400: {...}" into something a person can act on.
    throw new Error(claimErrorMessage(e))
  }
}

function claimErrorMessage(e: unknown): string {
  if (e instanceof APIError) {
    let code: string | undefined
    try {
      const parsed = e.body ? JSON.parse(e.body) : null
      code = parsed?.error
    } catch {
      // body wasn't JSON — fall through to status-based messaging
    }
    // Re-pairing into an existing screen needs a newer Hearth on the computer
    // serving the display; the code itself is fine and still usable.
    if (code === 'host_update_required') {
      return (
        "The computer running this display needs a Hearth update before a screen can be " +
        're-paired into an existing one. For now, pair it as a new screen: leave ' +
        '"re-pair an existing screen" unset and try the same code again.'
      )
    }
    // The relay answers a bad / expired / already-used code (and org-membership
    // or shape problems) with 400 {"error":"pairing_failed"}.
    if (e.status === 400 || code === 'pairing_failed') {
      return (
        "That code wasn't accepted. It may have expired or already been used — " +
        'in Home Assistant, restart the pairing to get a fresh code, then try again.'
      )
    }
    if (e.status === 401) {
      return "This phone can't claim devices right now — try signing out and back in."
    }
    // 404/405 mean the request never reached the pairing service (a routing or
    // config gap, not a bad code). Post-fix this shouldn't happen, but keep it
    // legible if it does.
    if (e.status === 404 || e.status === 405) {
      return `Couldn't reach the pairing service (HTTP ${e.status}). The server may need a moment — try again shortly.`
    }
    return `Pairing failed (HTTP ${e.status}). Please try again.`
  }
  return "Couldn't reach Hearth. Check your connection and try again."
}

// ---- Account deletion ---------------------------------------------------
// Relay account_delete.go + account_delete_cleanup.go: households the person
// is the only owner of are deleted the way "Delete household" deletes them;
// every other household they belong to is left, along with any computer of
// theirs serving it.

export type AffectedOrg = { id: string; name: string; member_count: number }

export type AccountDeletionPreview = {
  // The original shape, still sent; the wizard reads the fields below.
  affected_orgs: AffectedOrg[]
  // Absent from a relay that predates the account wizard.
  households_deleted?: { id: string; name: string; other_members: string[] }[]
  households_left?: { id: string; name: string }[]
  hosts?: {
    host_id: string
    hostname: string
    household: string
    household_deleted: boolean
    yours: boolean
    online: boolean
    auto_cleanup: boolean
  }[]
}

export type AccountDeletionResult = {
  ok: boolean
  hosts?: { host_id: string; hostname: string; household: string; cleaned_up: boolean }[] | null
  manual_steps?: HouseholdManualStep[] | null
}

export async function deleteAccountPreflight(): Promise<AccountDeletionPreview> {
  return postJSON(
    '/account/delete',
    { preflight: true },
    { kind: 'human_user', action: 'delete_account_preflight' },
    { auth: 'device' },
  )
}

// Permanently deletes the account. stepupToken comes from requestStepUpToken.
// Afterwards this device's credentials no longer exist: the caller shows the
// result, then calls logout() to wipe local state.
export async function deleteAccount(stepupToken: string): Promise<AccountDeletionResult> {
  try {
    return await postJSON(
      '/account/delete',
      { stepup_token: stepupToken },
      { kind: 'human_user', action: 'delete_account' },
      { auth: 'device' },
    )
  } catch (e) {
    throw new Error(householdDeleteErrorMessage(e))
  }
}

// ---- Household deletion -------------------------------------------------
// Permanent, owner-only, confirmed by typing the name and an email code. See
// relay household_delete.go and docs/household-deletion-plan.md.

export type HouseholdDeletionPreview = {
  organization_id: string
  name: string
  counts: {
    agents: number
    routines: number
    chat_rooms: number
    messages: number
    files: number
    connections: number
  }
  members: { human_user_id: string; name: string; email: string; role: string; is_you: boolean }[] | null
  hosts: { host_id: string; hostname: string; owner: string; online: boolean; auto_cleanup: boolean }[] | null
  devices: { id: string; name: string; form_factor: string }[] | null
  connections: { id: string; display_name: string; plugin: string }[] | null
  last_household: boolean
}

export type HouseholdManualStep = { kind: string; title: string; detail: string }

export type HouseholdDeletionResult = {
  ok: boolean
  hosts: { host_id: string; hostname: string; cleaned_up: boolean }[] | null
  manual_steps: HouseholdManualStep[] | null
}

export async function householdDeletionPreview(organizationID: string): Promise<HouseholdDeletionPreview> {
  try {
    return await postJSON(
      '/households/delete',
      { organization_id: organizationID, preflight: true },
      { kind: 'organization', id: organizationID, action: 'delete_preflight' },
      { auth: 'device' },
    )
  } catch (e) {
    throw new Error(householdDeleteErrorMessage(e))
  }
}

export async function deleteHousehold(args: {
  organizationID: string
  confirmName: string
  stepupToken: string
}): Promise<HouseholdDeletionResult> {
  try {
    return await postJSON(
      '/households/delete',
      {
        organization_id: args.organizationID,
        confirm_name: args.confirmName,
        stepup_token: args.stepupToken,
      },
      { kind: 'organization', id: args.organizationID, action: 'delete' },
      { auth: 'device' },
    )
  } catch (e) {
    throw new Error(householdDeleteErrorMessage(e))
  }
}

// The relay refuses with {"error": code, "message": words for a person}; show
// the words. Anything else is a transport or server failure.
export function householdDeleteErrorMessage(e: unknown): string {
  if (e instanceof APIError) {
    try {
      const parsed = e.body ? JSON.parse(e.body) : null
      if (parsed && typeof parsed.message === 'string' && parsed.message) return parsed.message
    } catch {
      // not JSON
    }
    if (e.status === 404 || e.status === 405) {
      return 'This server can’t delete households yet. Try again after it’s updated.'
    }
    return `That didn’t go through (HTTP ${e.status}). Please try again.`
  }
  return 'Couldn’t reach Hearth. Check your connection and try again.'
}

// ---- Device attestation (bridge pass-through) ---------------------------

export async function deviceCheckToken(): Promise<{
  token: string | null
  isSandbox: boolean
  supported: boolean
}> {
  return bridge.call('devicecheck.token')
}

export async function playIntegrityToken(): Promise<{
  token: string | null
  supported: boolean
}> {
  return bridge.call('playintegrity.token')
}
